import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import AuthContext from "../../hooks/authContext";
import useOnboardingProgress from "../../hooks/useOnboardingProgress";

const DEFAULT_ONBOARDING_EXPERIENCE = {
  loading: false,
  progress: {
    profileComplete: false,
    clientAdded: false,
    buildingAdded: false,
    crewAdded: false,
    shiftScheduled: false,
  },
  completedSteps: 0,
  totalSteps: 4,
  modalCompletedSteps: 0,
  modalTotalSteps: 4,
  modalProgressPercentage: 0,
  isComplete: false,
  hasShiftsBooked: false,
  isCompanyUser: false,
  userData: null,
  optionalProfileFieldsMissing: {
    companyAddress: false,
    companyZip: false,
  },
  optionalProfileFieldLabels: [],
  hasOptionalProfileFieldsMissing: false,
  isModalOpen: false,
  openModal: () => {},
  closeModal: () => {},
  dismissForSession: () => {},
  shouldShowIndicator: false,
  showCompletionIndicator: false,
};

const OnboardingExperienceContext = createContext(DEFAULT_ONBOARDING_EXPERIENCE);

const getDismissedSessionKey = (userId) => `onboarding_modal_dismissed_${userId}`;

export const OnboardingExperienceProvider = ({ children }) => {
  const auth = useContext(AuthContext);
  const location = useLocation();
  const isCompanyUser = auth?.userProfile?.role === "companyUser";
  const userId = isCompanyUser ? auth?.userProfile?.id : null;
  const onboarding = useOnboardingProgress(userId);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCompletionIndicator, setShowCompletionIndicator] = useState(false);
  const initialCompletionRef = useRef(null);
  const autoOpenTimeoutRef = useRef(null);

  const clearAutoOpenTimeout = useCallback(() => {
    if (autoOpenTimeoutRef.current) {
      clearTimeout(autoOpenTimeoutRef.current);
      autoOpenTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    clearAutoOpenTimeout();
    setIsModalOpen(false);
    setShowCompletionIndicator(false);
    initialCompletionRef.current = null;
  }, [clearAutoOpenTimeout, userId]);

  const dismissForSession = useCallback(() => {
    clearAutoOpenTimeout();
    if (userId) {
      sessionStorage.setItem(getDismissedSessionKey(userId), "true");
    }
    setIsModalOpen(false);
  }, [clearAutoOpenTimeout, userId]);

  const openModal = useCallback(() => {
    clearAutoOpenTimeout();
    setIsModalOpen(true);
  }, [clearAutoOpenTimeout]);

  const closeModal = useCallback(() => {
    clearAutoOpenTimeout();
    setIsModalOpen(false);
  }, [clearAutoOpenTimeout]);

  useEffect(() => {
    if (!userId || onboarding.loading || !onboarding.isCompanyUser) {
      return;
    }

    const dismissedKey = getDismissedSessionKey(userId);

    if (initialCompletionRef.current === null) {
      initialCompletionRef.current = onboarding.isComplete;

      if (onboarding.isComplete) {
        clearAutoOpenTimeout();
        setShowCompletionIndicator(false);
        return;
      }

      if (!onboarding.isComplete && sessionStorage.getItem(dismissedKey) !== "true") {
        const isDashboardRoute = location?.pathname === "/" || location?.pathname === "/dashboard";
        const isMobileViewport =
          typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;
        if (isDashboardRoute && !isMobileViewport) {
          clearAutoOpenTimeout();
          autoOpenTimeoutRef.current = setTimeout(() => {
            setIsModalOpen(true);
            autoOpenTimeoutRef.current = null;
          }, 450);
        }
      }

      setShowCompletionIndicator(false);
      return;
    }

    if (!initialCompletionRef.current && onboarding.isComplete) {
      clearAutoOpenTimeout();
      setIsModalOpen(false);
      setShowCompletionIndicator(true);
      return;
    }

    if (!onboarding.isComplete) {
      setShowCompletionIndicator(false);
    }
  }, [
    clearAutoOpenTimeout,
    onboarding.isCompanyUser,
    onboarding.isComplete,
    onboarding.loading,
    userId,
    location?.pathname,
  ]);

  useEffect(() => () => clearAutoOpenTimeout(), [clearAutoOpenTimeout]);

  const shouldShowIndicator = useMemo(() => {
    if (!userId || onboarding.loading || !onboarding.isCompanyUser) {
      return false;
    }

    return !onboarding.isComplete || showCompletionIndicator;
  }, [
    onboarding.isCompanyUser,
    onboarding.isComplete,
    onboarding.loading,
    showCompletionIndicator,
    userId,
  ]);

  const value = useMemo(
    () => ({
      ...onboarding,
      hasShiftsBooked: onboarding.progress.shiftScheduled,
      isModalOpen,
      openModal,
      closeModal,
      dismissForSession,
      shouldShowIndicator,
      showCompletionIndicator,
    }),
    [
      dismissForSession,
      isModalOpen,
      onboarding,
      openModal,
      closeModal,
      shouldShowIndicator,
      showCompletionIndicator,
    ]
  );

  return (
    <OnboardingExperienceContext.Provider value={value}>
      {children}
    </OnboardingExperienceContext.Provider>
  );
};

export const useOnboardingExperience = () => {
  const context = useContext(OnboardingExperienceContext);
  return context;
};
